import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Bookmark, CalendarDays, MapPin, Navigation, Share2, Star } from "lucide-react";

import "./FeedDetails.css";
import { FeedDetailsStore } from "./FeedDetailsStore";
import { BaseService } from "../../Base/BaseService";
import { AnalyticsSource, withSource } from "../../AnalyticsClient/AnalyticsSources";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { DelayedLookstyLoader } from "../../GloablComponents/Loader/DelayedLookstyLoader";
import { MasonryGrid } from "../../GloablComponents/Masonry/MasonryGrid";
import { MasonryPostCard } from "../../GloablComponents/Masonry/MasonryPostCard";

export const FeedDetailsPage = observer(() => {
  const navigate = useNavigate();
  const { id } = useParams();

  const pageRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSimilarRef = useRef<HTMLDivElement | null>(null);

  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  const [searchParams] = useSearchParams();
  const trackedPostIdRef = useRef<number | null>(null);

  const store = useMemo(() => new FeedDetailsStore(), []);
  const [copyMessage, setCopyMessage] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    store.load(Number(id));
  }, [id, store]);

  useEffect(() => {
    if (!store.SimilarHasMore) return;

    const element = loadMoreSimilarRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          store.loadMoreSimilar();
        }
      },
      {
        root: null,
        rootMargin: "140px",
        threshold: 0.1,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [store, store.SimilarHasMore]);

  useEffect(() => {
    if (!store.Item) return;
    if (trackedPostIdRef.current === store.Item.id) return;

    trackedPostIdRef.current = store.Item.id;

    AnalyticsClient.track({
      eventType: "post_opened",
      source: searchParams.get("source") || AnalyticsSource.Direct,
      entityType: "post",
      entityId: store.Item.id,
      metadata: {
        masterId: store.Item.masterId,
        serviceId: store.Item.serviceId,
      },
    });
  }, [store.Item?.id, searchParams, store.Item]);

  const handleTouchStart = (event: React.TouchEvent) => {
    if (window.scrollY > 0) return;

    startY.current = event.touches[0].clientY;
    currentY.current = 0;
    isDragging.current = true;

    if (pageRef.current) {
      pageRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!isDragging.current || !pageRef.current) return;

    const delta = event.touches[0].clientY - startY.current;

    if (delta <= 0) return;

    currentY.current = delta;
    pageRef.current.style.transform = `translateY(${delta}px)`;
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !pageRef.current) return;

    isDragging.current = false;
    pageRef.current.style.transition = "transform 0.22s ease";

    if (currentY.current > 120) {
      navigate(-1);
      return;
    }

    pageRef.current.style.transform = "translateY(0)";
  };

  if (store.IsLoading) {
    return (
      <div className="feed-details-page">
        <DelayedLookstyLoader variant="details" title="Открываем работу" subtitle="Готовим фото, цену и мастера" show={store.IsLoading} />
      </div>
    );
  }

  if (!store.Item) {
    return <div className="feed-details-page">Идея не найдена</div>;
  }

  const item = store.Item;

  const masterName = store.Master?.name || item.masterName || "Мастер";

  const avatarUrl = store.Master?.avatarUrl ? BaseService.GetFileUrl(store.Master.avatarUrl) : "";

  const avatarLetter = masterName[0].toUpperCase();

  const masterAddress = [store.Master?.location, store.Master?.address].filter((value) => value?.trim()).join(", ");

  const masterDistanceText = item.distanceText?.trim() ?? "";

  const formatReviewsText = (count: number) => {
    const value = Math.abs(count) % 100;
    const lastDigit = value % 10;

    if (value > 10 && value < 20) return "отзывов";
    if (lastDigit === 1) return "отзыв";
    if (lastDigit >= 2 && lastDigit <= 4) return "отзыва";

    return "отзывов";
  };

  const ratingValue = store.Rating?.averageRating ?? 0;
  const reviewsCount = store.Rating?.reviewsCount ?? 0;

  const copyPostLink = async () => {
    const url = `${window.location.origin}${withSource(`/feed/${item.id}`, AnalyticsSource.Share)}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopyMessage("Ссылка скопирована");
    } catch {
      setCopyMessage("Не удалось скопировать ссылку");
    }

    window.setTimeout(() => {
      setCopyMessage("");
    }, 2200);
  };

  return (
    <>
      {copyMessage && <div className="feed-details-toast">{copyMessage}</div>}

      <div ref={pageRef} className="feed-details-page" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div className="feed-details-main">
          <div className="feed-details-media">
            <img className="feed-details-image" src={BaseService.GetFileUrl(item.imageUrl)} alt={item.title} fetchPriority="high" decoding="async" />

            <div className="feed-details-top-actions">
              <button type="button" className="feed-details-nav-button" onClick={() => navigate(-1)} aria-label="Назад">
                <ArrowLeft size={22} />
              </button>

              <button type="button" className="feed-details-nav-button" onClick={copyPostLink} aria-label="Поделиться">
                <Share2 size={21} />
              </button>
            </div>
          </div>

          <div className="feed-details-content">
            {item.categoryName && <div className="feed-details-category">{item.categoryName}</div>}

            <h1>{item.title}</h1>

            {(store.PriceText || masterDistanceText || store.Master) && (
              <div className="feed-details-price-box">
                {store.PriceText && (
                  <div className="feed-details-price-row">
                    <strong>{store.PriceText}</strong>
                  </div>
                )}

                {(masterDistanceText || store.Master) && (
                  <div className="feed-details-price-badges">
                    {masterDistanceText && (
                      <span className="feed-details-distance-badge">
                        <Navigation size={13} />

                        <span>{masterDistanceText}</span>
                      </span>
                    )}

                    {store.Master && <span className="feed-details-booking-badge">{store.Master.hasSchedule ? "Онлайн-запись" : "Заявка мастеру"}</span>}
                  </div>
                )}
              </div>
            )}

            {item.description && <p>{item.description}</p>}

            <div className="feed-details-master" onClick={() => navigate(withSource(`/master/${item.masterId}`, AnalyticsSource.FeedDetails))}>
              <div className="feed-details-avatar">{avatarUrl ? <img src={avatarUrl} alt={masterName} /> : avatarLetter}</div>

              <div className="feed-details-master-info">
                <div className="feed-details-master-top">
                  <strong>{masterName}</strong>

                  {reviewsCount > 0 && (
                    <span className="feed-details-rating">
                      <Star size={13} />
                      {ratingValue.toFixed(1)} · {reviewsCount} {formatReviewsText(reviewsCount)}
                    </span>
                  )}
                </div>

                {masterAddress && (
                  <span className="feed-details-location" title={masterAddress}>
                    <MapPin size={13} />

                    <span>{masterAddress}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="feed-details-actions">
              <button className={store.IsFavorite ? "secondary saved" : "secondary"} disabled={store.IsFavoriteLoading} onClick={() => store.toggleFavorite()}>
                <Bookmark size={20} fill={store.IsFavorite ? "currentColor" : "none"} />

                <span>{store.IsFavorite ? "Убрать" : "Сохранить"}</span>
              </button>

              <button
                className="primary"
                disabled={!store.Master}
                onClick={() => {
                  if (!store.Master) return;

                  if (store.Master.hasSchedule) {
                    navigate(withSource(`/master/${store.Master.id}/booking`, AnalyticsSource.FeedDetails));

                    return;
                  }

                  navigate(withSource(`/master/${store.Master.id}/booking-request`, AnalyticsSource.FeedDetails));
                }}
              >
                <CalendarDays size={19} />

                <span>{store.Master?.hasSchedule ? "Записаться" : "Оставить заявку"}</span>
              </button>
            </div>

            {store.FavoriteError && <div className="feed-details-error">{store.FavoriteError}</div>}
          </div>
        </div>

        <section className="feed-details-similar">
          <h2>Похожие идеи</h2>

          {store.IsLoadingSimilar && !store.SimilarItems.length && <div className="feed-details-message">Загружаем похожие идеи...</div>}

          {store.SimilarError && <div className="feed-details-message error">{store.SimilarError}</div>}

          {!store.IsLoadingSimilar && !store.SimilarError && !store.SimilarItems.length && <div className="feed-details-message">Похожих идей пока нет</div>}

          {!!store.SimilarItems.length && (
            <>
              <MasonryGrid className="feed-details-grid">
                {store.SimilarItems.map((similarItem) => (
                  <MasonryPostCard key={similarItem.id} item={similarItem} className="feed-details-card" onClick={() => navigate(withSource(`/feed/${similarItem.id}`, AnalyticsSource.FeedDetails))} />
                ))}
              </MasonryGrid>

              {store.SimilarHasMore && (
                <div ref={loadMoreSimilarRef} className="feed-details-loader">
                  {store.IsLoadingMoreSimilar ? "Загружаем..." : ""}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
});
